import "./polyfill";
import { createApp, reactive } from "vue";
import { createStore } from "vuex";
import App from "./App";
import router from "./router/index";
import miniToastr from "mini-toastr";
import API from "./api/stalk.net";
import authSession from "./api/authSession";
import { getCurrentLocale, getLocaleTag, i18n, setI18nLocale } from "./i18n";
import NotificationsPlugin from "./plugins/notifications";
import { resolveToastMessage } from "./shared/toast";

miniToastr.init({
  types: {
    success: "success",
    error: "error",
    info: "info",
    warn: "warn"
  }
});

function normalizeUserGroup(user) {
  if (!user || typeof user !== "object") {
    return "";
  }

  return String(
    user.Group ||
      user.group ||
      user.UserGroup ||
      user.user_group ||
      authSession.getUserGroup() ||
      ""
  ).toLowerCase();
}

let resolver;
const store = createStore({
  state() {
    return {
      user: null,
      member: null,
      dataLoadingError: null,
      dataLoading: new Promise((resolve, reject) => {
        resolver = resolve;
      })
    };
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setMember(state, member) {
      state.member = member;
    },
    setDataLoadingError(state, error) {
      state.dataLoadingError = error || null;
    },
    doneLoading() {
      resolver();
    },
    errorLoading(state, error) {
      state.dataLoadingError = error || null;
      resolver();
    }
  },
  actions: {
    async loadUser({ commit }) {
      try {
        const resp = await API.private.getSelfUserInfo();
        const user = {
          ...(resp.data || {})
        };
        const normalizedGroup = normalizeUserGroup(user);
        if (normalizedGroup) {
          user.Group = normalizedGroup;
        }
        commit("setDataLoadingError", null);
        commit("setUser", user);
      } catch (error) {
        commit("setUser", null);
        commit("errorLoading", error);
      }
    },
    async loadMember({ state, commit }) {
      try {
        if (!state.user) {
          throw { response: { data: "ERR_NOT_ALLOWED" } };
        }
        const resp = await API.private.getMemberInfo(state.user.Id);
        commit("setMember", resp.data);
        commit("setDataLoadingError", null);
        commit("doneLoading");
      } catch (error) {
        commit("setMember", null);
        commit("errorLoading", error);
      }
    }
  }
});

function toast({ title, message, type, timeout, cb }) {
  const msg = resolveToastMessage(message, type, i18n);
  return miniToastr[type](msg, title, timeout, cb);
}

function notify(type, message, title, timeout, cb) {
  return toast({
    type,
    message,
    title,
    timeout,
    cb
  });
}

const filters = {
  formatDate(value) {
    return new Date(value * 1000).toLocaleDateString(
      getLocaleTag(getCurrentLocale())
    );
  },
  formatDateTime(value) {
    const d = new Date(value * 1000);
    const localeTag = getLocaleTag(getCurrentLocale());
    return `${d.toLocaleDateString(localeTag)} ${d.toLocaleTimeString(
      localeTag
    )}`;
  },
  formatCurrency(value) {
    if (typeof value !== "number") {
      return value;
    }
    const formatter = new Intl.NumberFormat(getLocaleTag(getCurrentLocale()), {
      style: "currency",
      currency: "UAH",
      minimumFractionDigits: 0
    });
    return formatter.format(value);
  },
  limitString(value) {
    const maxSymbols = 10;
    if (typeof value === "string" && value.length > maxSymbols) {
      return `${value.slice(0, maxSymbols)}...`;
    }
    return value;
  }
};

const app = createApp(App);

app.use(store);
app.use(router);
app.use(i18n);
app.use(NotificationsPlugin, {
  success: toast,
  error: toast,
  info: toast,
  warn: toast
});

const i18nBridge = reactive({
  get locale() {
    return getCurrentLocale();
  },
  set locale(locale) {
    setI18nLocale(locale);
  }
});

app.config.globalProperties.$i18n = i18nBridge;

app.config.globalProperties.$formatDate = filters.formatDate;
app.config.globalProperties.$formatDateTime = filters.formatDateTime;
app.config.globalProperties.$formatCurrency = filters.formatCurrency;
app.config.globalProperties.$limitString = filters.limitString;
app.config.globalProperties.$notify = notify;
app.config.globalProperties.$notifySuccess = (message, title, timeout, cb) =>
  notify("success", message, title, timeout, cb);
app.config.globalProperties.$notifyError = (message, title, timeout, cb) =>
  notify("error", message, title, timeout, cb);
app.config.globalProperties.$notifyInfo = (message, title, timeout, cb) =>
  notify("info", message, title, timeout, cb);
app.config.globalProperties.$notifyWarn = (message, title, timeout, cb) =>
  notify("warn", message, title, timeout, cb);

app.mount("#app");

